import type { Geometry, LineString } from "geojson";
import { z } from "zod";

import type { ItinerarySegment } from "@mountayaapp/types/api/api";
import type { ActivitySlug, SuggestionRoute, SuggestionsInput } from "@mountayaapp/types/api/data";

import type { ItineraryViewSource } from "../studio/itineraryView";
import {
  graphqlRouteToLiveRoute,
  liveSegmentRouteAnalysisSchema,
  liveSegmentRouteElevationSchema,
  overlayRouteAnalysis,
} from "../studio/liveSegment";
import { assignDistinctColors, hexColorSchema, LIVELY_PALETTE } from "../studio/segmentColors";
import type { ItineraryDraft } from "./importItinerary";

/**
 * suggestRouteTypeSchema names the two shapes a suggested route can take: a
 * `loop` that returns to its start point (the default) or a `point_to_point`
 * line shaped to meet the target distance between a start and a chosen end.
 * Zod-first because the value drives whether the end-point field is revealed and
 * whether the generated input carries an `end`, and it is shared with the
 * route-type setter.
 */
export const suggestRouteTypeSchema = z.enum(["loop", "point_to_point"]);

/**
 * SuggestRouteType names a suggested route's shape, derived from
 * suggestRouteTypeSchema.
 */
export type SuggestRouteType = z.infer<typeof suggestRouteTypeSchema>;

/**
 * pickTargetSchema names which endpoint a map-pick session is setting — the
 * `start` point (always pickable) or the `end` point (only for a point-to-point
 * route). Zod-first because the value selects the cursor tooltip text and the
 * setter the click resolves to, and it is shared with the begin-pick action; a
 * null picking slot (no pick armed) lives outside the schema as the absence of a
 * value.
 */
export const pickTargetSchema = z.enum(["start", "end"]);

/**
 * PickTarget names the endpoint a map-pick session sets, derived from
 * pickTargetSchema.
 */
export type PickTarget = z.infer<typeof pickTargetSchema>;

/**
 * suggestionStatusSchema is the suggest flow's progress, a discriminated union
 * on `kind`: `idle` (the form is open, nothing generated), `generating` (a
 * suggestions request is in flight), `ready` (candidates resolved and are being
 * browsed), and an error carrying a human-readable `message`. Zod-first because
 * the value drives the generate button + banner state and is shared with the
 * status-set action, mirroring importSubmissionStatusSchema.
 */
export const suggestionStatusSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("idle") }),
  z.object({ kind: z.literal("generating") }),
  z.object({ kind: z.literal("ready") }),
  z.object({ kind: z.literal("error"), message: z.string().min(1) }),
]);

/**
 * SuggestionStatus is the suggest flow's progress state, derived from
 * suggestionStatusSchema.
 */
export type SuggestionStatus = z.infer<typeof suggestionStatusSchema>;

/**
 * geometrySchema validates a value is a GeoJSON Geometry at the suggestion
 * boundary: an object carrying a `type` discriminator. The full coordinate
 * structure is NOT re-validated — GeoJSON is structurally large and trusted
 * in-process, the same stance the import draft and the segment edit session take
 * on their geometry — so this is a shallow guard that rejects a non-geometry (a
 * string, null, a bare object) before it lands in a candidate, then carries the
 * value as the `@types/geojson` Geometry the map preview reads.
 */
const geometrySchema = z.custom<Geometry>(
  (value): value is Geometry =>
    typeof value === "object" &&
    value !== null &&
    typeof (value as { type?: unknown }).type === "string",
  { message: "expected a GeoJSON Geometry" },
);

/**
 * suggestionCandidateSchema is one generated route option the suggest results
 * list renders: the deterministic `seed` the server reproduces it from (the key
 * the keep/regenerate cycle pins on), the summary metrics (`distanceMeters`,
 * `durationSeconds`, `ascentMeters`, `descentMeters`) the row reads, the route
 * `geometry` the preview draws, the full `elevation` summary and surface/way-type/
 * slope `analysis` the selected-candidate profile renders (already normalized to
 * the persisted segment's snake_case indices + REST enums by the wire
 * conversion), the server's human-readable `summary`, the distinct, vivid `color`
 * the row swatch and the map preview line paint it in (so each option reads as its
 * own route at a glance), and whether the user has marked it `kept` so a regenerate
 * reproduces it. Zod-first because the candidate is validated at the wire→session
 * boundary.
 */
export const suggestionCandidateSchema = z.object({
  analysis: liveSegmentRouteAnalysisSchema,
  ascentMeters: z.number(),
  color: hexColorSchema,
  descentMeters: z.number(),
  distanceMeters: z.number(),
  durationSeconds: z.number(),
  elevation: liveSegmentRouteElevationSchema,
  geometry: geometrySchema,
  kept: z.boolean().default(false),
  seed: z.number().int(),
  summary: z.string(),
});

/**
 * SuggestionCandidate is one generated route option, derived from
 * suggestionCandidateSchema.
 */
export type SuggestionCandidate = z.infer<typeof suggestionCandidateSchema>;

/**
 * SUGGESTION_TARGET_COUNT is the number of route candidates the suggest flow
 * always asks the server for in one generation — the `candidates` value on every
 * SuggestionsInput it builds. The keep/regenerate cycle holds this constant: a
 * regenerate reproduces the kept seeds and fills the rest with fresh ones up to
 * this total, so the list size never changes.
 */
export const SUGGESTION_TARGET_COUNT = 5;

/**
 * SUGGESTION_MAX_KEPT is the most candidates a user may pin to keep across a
 * regenerate. The keep toggle enforces it — a flip that would exceed the cap is a
 * no-op — so a regenerate always leaves at least two fresh slots in the
 * SUGGESTION_TARGET_COUNT result.
 */
export const SUGGESTION_MAX_KEPT = 3;

/**
 * SuggestionSession is the open suggest shell state: the resolved activity
 * (null until the user picks one), the route `routeType` (loop default vs.
 * point-to-point), the target `distanceMeters`, the optional `ascentMeters` and
 * `speedKmh` overrides (null = use the activity default), the `start` and `end`
 * map points (each null until picked; `end` only used for a point-to-point
 * route), which endpoint a map-pick is currently arming (`picking`, null = no
 * pick armed), the generated `candidates`, the accumulated `seenSeeds` across
 * every generation (so a regenerate can exclude what was already shown), the
 * `selectedSeed` the map preview draws (null = none previewed), whether the last
 * generation came back short of the requested count (`exhausted`, the pool is
 * spent for the current constraints), and the generation `status`.
 *
 * Beside the route-generation inputs it carries the draft an itinerary is
 * committed from: the editable `draft` (null until a chosen route is shaped into
 * one), the editable itinerary `name`, and the ownership the commit persists under
 * — the owning `organizationId` (null = the backend's default org), the
 * `isPrivate` visibility flag, and the `collectionIds` the committed itinerary is
 * attached to. These mirror the import session's commit-time fields exactly: the
 * draft session owns the ownership choice, resolved at commit, so the shell sets
 * name/visibility/owner/collections in place before the draft is persisted.
 *
 * Mirrors the import session's "plain serializable view" split — no map handle or
 * non-serializable value lives here, every field is plain data set only through
 * the tool bus.
 */
export interface SuggestionSession {
  activityId: string | null;
  /**
   * agentSelected records that the co-pilot's run deliberately selected a
   * candidate since the last generation — the act a delegated "choose for me"
   * flow performs and an uninvited staging skips. The agent-origin startFrom
   * gate reads it, so a run cannot stage a route straight off a generate while
   * the conversation's card is still awaiting the user's pick. Seeded false;
   * an agent-origin select sets it, a generation clears it (a fresh batch
   * stales any delegated pick). Human dispatches never touch it.
   */
  agentSelected: boolean;
  ascentMeters: number | null;
  candidates: SuggestionCandidate[];
  /**
   * collectionIds is the set of collections the committed itinerary is added to
   * once it lands. Seeded empty; the shell's collection picker writes the chosen
   * ids and the commit attaches them best-effort after the create. Like the name +
   * privacy + owner, it does not change what a generation resolves.
   */
  collectionIds: string[];
  distanceMeters: number;
  /**
   * draft is the editable itinerary draft the commit persists — the working copy a
   * chosen suggested route is shaped into (a single directions segment), edited and
   * committed through the same code paths an import draft uses. Null until a route
   * is shaped into one.
   */
  draft: ItineraryDraft | null;
  end: [number, number] | null;
  /**
   * exhausted is true when the last generation returned FEWER routes than the
   * requested SUGGESTION_TARGET_COUNT — the signal that the data API has no more
   * distinct routes for the current constraints. The form reads it to nudge the
   * user to widen the distance or ascent and to gate a futile regenerate under the
   * same constraints. Seeded false on open and reset to false whenever the
   * candidates are cleared (any constraint change), so editing an input lifts it.
   */
  exhausted: boolean;
  /**
   * isPrivate is whether the commit persists the itinerary as private. Seeded to
   * match the itinerary CREATE flow's default (private); the shell's privacy
   * control flips it and the commit reads it.
   */
  isPrivate: boolean;
  /** name is the editable itinerary name the commit persists (2–30 chars at commit). */
  name: string;
  /**
   * organizationId is the organization that will own the committed itinerary, or
   * null to let the backend default to the authenticated user's organization. The
   * shell's OrganizationSelect writes it; the commit persists under it.
   */
  organizationId: string | null;
  picking: PickTarget | null;
  routeType: SuggestRouteType;
  seenSeeds: number[];
  selectedSeed: number | null;
  speedKmh: number | null;
  /**
   * targetSegmentId is the existing segment this batch was generated to REPLACE,
   * or null when it was generated to add a new one. It is what makes a pick mean
   * something: the same candidate is a reroute of segment X or an appended
   * segment depending only on what the user asked for, and the run — the one
   * thing that understood the ask — records it here when it generates. Null is
   * the honest default; the picking surface falls back to the live selection.
   */
  targetSegmentId: string | null;
  start: [number, number] | null;
  status: SuggestionStatus;
}

/**
 * candidateFromRoute projects one wire SuggestionRoute into the session's
 * SuggestionCandidate: it carries the seed, the summary metrics (the elevation
 * summary's ascent/descent become the candidate's ascentMeters/descentMeters),
 * the route geometry (cast to the `@types/geojson` Geometry the preview reads,
 * the same boundary cast itinerary geometry takes), the server summary, and the
 * full elevation summary + surface/way-type/slope analysis — converted to the
 * persisted segment's snake_case indices + REST enums by the shared
 * graphqlRouteToLiveRoute, so the selected-candidate profile and the started-from
 * draft project them onto an ItinerarySegment without a second translation. The
 * `color` the row swatch and preview line paint the candidate in is supplied by the
 * caller (mergeCandidates assigns it across the batch), not derived from the wire —
 * the wire carries no color. It opens the candidate unkept (`kept: false`) — the
 * keep flag is a user choice the merge preserves, never something the wire carries.
 */
export const candidateFromRoute = (route: SuggestionRoute, color: string): SuggestionCandidate => {
  // Reuse the shared wire→live-route conversion for the elevation + analysis
  // blocks. The candidate keeps the route's own geometry (possibly a
  // GeometryCollection); the converter's geometry passthrough is unused here, so
  // the cast only satisfies its LineString input.
  const live = graphqlRouteToLiveRoute({
    analysis: route.analysis,
    distance: route.distance,
    duration: route.duration,
    elevation: route.elevation,
    geometry: route.geometry as LineString,
  });

  return {
    analysis: live.analysis ?? {
      slopeInfo: [],
      surfaceInfo: [],
      trailDifficultyInfo: [],
      waytypeInfo: [],
    },
    ascentMeters: route.elevation.ascent,
    color,
    descentMeters: route.elevation.descent,
    distanceMeters: route.distance,
    durationSeconds: route.duration,
    elevation: {
      altitudeMax: route.elevation.altitude.max,
      altitudeMin: route.elevation.altitude.min,
      ascent: route.elevation.ascent,
      descent: route.elevation.descent,
      gradientMax: route.elevation.gradient.max,
      gradientMin: route.elevation.gradient.min,
    },
    geometry: route.geometry as Geometry,
    kept: false,
    seed: route.seed,
    summary: route.summary,
  };
};

/**
 * mergeCandidates folds a fresh batch of wire routes into the session's
 * candidate list, preserving the user's keep choices, assigning each option a
 * distinct color, and accumulating the set of seeds ever seen. Each returned route
 * projects to a candidate; a route whose seed was kept in `prevCandidates` carries
 * `kept: true` through AND retains that prior candidate's color (both matched by
 * seed), so a regenerate that reproduces a pinned route keeps it pinned in the same
 * hue the user has been looking at. Every FRESH candidate (a seed not carried over
 * from a kept one) gets a vivid color distinct from every retained color and from
 * the other fresh candidates, spread across the hue wheel by assignDistinctColors,
 * so the rows and the map preview read each route as its own. The returned order is
 * the wire order (the server places the included seeds first), and `seenSeeds` is
 * the union of `prevSeen` with every returned seed — the exclude pool a later
 * regenerate draws on so fresh candidates never repeat a shown one. Pure and
 * deterministic — it reads no store and draws no random color, so the generate tool
 * can build the next session state reproducibly from the response it holds.
 */
export const mergeCandidates = (
  prevCandidates: SuggestionCandidate[],
  prevSeen: number[],
  routes: SuggestionRoute[],
): { candidates: SuggestionCandidate[]; seenSeeds: number[] } => {
  const keptBySeed = new Map<number, SuggestionCandidate>(
    prevCandidates
      .filter((candidate) => candidate.kept)
      .map((candidate) => [candidate.seed, candidate]),
  );

  // The colors a regenerate carries over from kept candidates are reserved: the
  // fresh candidates must avoid them so a kept hue is never duplicated by a new one.
  const retainedColors = routes
    .map((route) => keptBySeed.get(route.seed)?.color)
    .filter((color): color is string => color !== undefined);

  // One distinct, wheel-spread color per fresh candidate, avoiding the retained
  // ones. Consumed in wire order by a cursor as the routes are projected below.
  const freshCount = routes.filter((route) => !keptBySeed.has(route.seed)).length;
  const freshColors = assignDistinctColors(freshCount, retainedColors);
  let freshCursor = 0;

  const candidates = routes.map((route) => {
    const kept = keptBySeed.get(route.seed);
    if (kept) {
      // A reproduced kept route keeps its pin and its prior color (the hue the user
      // has been looking at), so a regenerate does not recolor a pinned option.
      return { ...candidateFromRoute(route, kept.color), kept: true };
    }

    // A fresh route takes the next distinct color off the batch assignment.
    const color = freshColors[freshCursor] ?? LIVELY_PALETTE[0];
    freshCursor += 1;

    return candidateFromRoute(route, color);
  });

  // Accumulate the seeds ever shown: the prior set plus every returned seed,
  // de-duplicated so a reproduced seed is not listed twice. A later regenerate
  // excludes this set (minus the kept seeds) so fresh candidates never repeat.
  const seenSeeds = [...prevSeen];
  const seen = new Set<number>(prevSeen);
  for (const route of routes) {
    if (!seen.has(route.seed)) {
      seen.add(route.seed);
      seenSeeds.push(route.seed);
    }
  }

  return { candidates, seenSeeds };
};

/**
 * buildSuggestionsInput projects the session plus the already-resolved data-API
 * activity slug into the wire SuggestionsInput the generate tool sends. It asks
 * for SUGGESTION_TARGET_COUNT candidates every time; `start` is asserted
 * non-null (the caller gates generation on a set start, so a null start is a
 * programming error, not a user state); `end` rides through only for a
 * point-to-point route with a set end (a loop omits it, looping back to the
 * start); `ascent` is omitted when null (the server uses the activity default).
 * `speed` falls back from the user override to the resolved activity default
 * (`defaultSpeedKmh`), and is omitted only when neither is set: the override is
 * the user's explicit choice, the default keeps the server's duration estimate
 * meaningful when the user left speed unset (sending no speed makes the server
 * compute a near-zero duration, so the row reads "0 min" for every candidate).
 * The activity default is taken already-resolved (the tool reads it off the
 * activity's capabilities), so core stays free of the catalog lookup, the same
 * split the activity slug takes. The seed directives reproduce the
 * keep/regenerate contract: `include` is the kept seeds (reproduced first),
 * `exclude` is every other seen seed (the fresh pool avoids them), and `seeds` is
 * omitted entirely when both lists are empty — the initial generation. The
 * activity is taken already-resolved (UUID → slug → toDataActivity happens in the
 * tool), so core stays free of that mapping, the same split directions takes.
 */
export const buildSuggestionsInput = (
  session: SuggestionSession,
  activity: ActivitySlug,
  defaultSpeedKmh: number | null,
): SuggestionsInput => {
  if (session.start === null) {
    throw new Error("buildSuggestionsInput: start point is required");
  }

  // include = the kept seeds (reproduced first); exclude = every other seen seed
  // so the fresh candidates never repeat one already shown.
  const include = session.candidates
    .filter((candidate) => candidate.kept)
    .map((candidate) => candidate.seed);
  const included = new Set<number>(include);
  const exclude = session.seenSeeds.filter((seed) => !included.has(seed));

  return {
    activity,
    ascent: session.ascentMeters ?? undefined,
    candidates: SUGGESTION_TARGET_COUNT,
    distance: session.distanceMeters,
    end: session.routeType === "point_to_point" && session.end ? session.end : undefined,
    seeds: include.length || exclude.length ? { exclude, include } : undefined,
    // The user override wins; absent it the resolved activity default keeps the
    // server's duration estimate meaningful; absent both the field is omitted.
    speed: session.speedKmh ?? defaultSpeedKmh ?? undefined,
    start: session.start,
  };
};

/**
 * selectedCandidate returns the candidate the session's `selectedSeed` names, or
 * null when nothing is selected (or the selected seed is no longer in the list,
 * e.g. a regenerate dropped a fresh-but-unkept candidate). It is the single
 * reader the map preview-sync and the "start from this itinerary" hand-off share
 * to resolve the chosen route from the session.
 */
export const selectedCandidate = (session: SuggestionSession): SuggestionCandidate | null => {
  if (session.selectedSeed === null) {
    return null;
  }

  return session.candidates.find((candidate) => candidate.seed === session.selectedSeed) ?? null;
};

/**
 * suggestionCandidateToView projects a selected suggestion candidate into the
 * ItineraryViewSource the docked elevation panel renders — one synthetic
 * directions segment carrying the candidate geometry plus its elevation summary
 * and surface/way-type/slope analysis, so selecting a candidate shows its full
 * profile (chart + the three bars) before the user starts from it. The base is
 * the neutral seed draftSegmentToItinerary uses (id "0", empty itinerary scope, a
 * date-zero stamp), onto which overlayRouteAnalysis sets the altitude/gradient
 * fields + the analysis bands; the candidate distance/duration and the candidate
 * geometry (kept as-is — it may be a GeometryCollection, which the profile's
 * extractLineStrings flattens) ride on top. The view id is null (a preview has no
 * server id), so the panel renders it as a preview with no actions menu.
 */
export const suggestionCandidateToView = (candidate: SuggestionCandidate): ItineraryViewSource => {
  const base: ItinerarySegment = {
    altitude_max: null,
    altitude_min: null,
    ascent: null,
    color: null,
    created_at: new Date(0),
    created_by: { id: "" },
    descent: null,
    description: null,
    distance: candidate.distanceMeters,
    duration: candidate.durationSeconds,
    geojson: candidate.geometry,
    gradient_max: null,
    gradient_min: null,
    group_id: null,
    id: "0",
    itinerary_id: "",
    mode: "directions",
    name: "",
    position: 0,
    revision: 0,
    slope_info: [],
    speed: null,
    start_at: null,
    steps: [],
    surface_info: [],
    trail_difficulty_info: [],
    waytype_info: [],
  };

  return {
    activity_id: null,
    id: null,
    segments: [overlayRouteAnalysis(base, candidate)],
    waypoints: [],
  };
};
